import { useEffect, useState } from 'react';
import { Mail, Plus, X, Save } from 'lucide-react';
import { settingsAPI } from '../services/api.js';

// Scheduled EMAIL CALL RECAPS — the user picks times of day to get an emailed recap
// of their recent calls. Reads/writes /settings/email-recap; the core scheduler
// sends them. Times are stored HH:MM (24h); shown 12h.

const TIMEZONES = [
  ['America/New_York', 'Eastern'],
  ['America/Chicago', 'Central'],
  ['America/Denver', 'Mountain'],
  ['America/Phoenix', 'Arizona'],
  ['America/Los_Angeles', 'Pacific'],
  ['UTC', 'UTC']
];

// '17:00' -> '5:00 PM'
function pretty(hhmm) {
  const [h, m] = hhmm.split(':').map(Number);
  const ap = h < 12 ? 'AM' : 'PM';
  const h12 = ((h + 11) % 12) + 1;
  return `${h12}:${String(m).padStart(2, '0')} ${ap}`;
}

export default function EmailRecapsCard() {
  const [state, setState] = useState({ enabled: false, email: '', timezone: 'America/Chicago', times: [] });
  const [newTime, setNewTime] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [savedOk, setSavedOk] = useState(false);
  const [notReady, setNotReady] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await settingsAPI.getEmailRecap();
      const d = r?.data || {};
      setState({
        enabled: !!d.enabled,
        email: d.email || '',
        timezone: d.timezone || 'America/Chicago',
        times: Array.isArray(d.times) ? d.times : []
      });
    } catch (err) {
      if (err.status === 404) setNotReady(true);
      else setError(err.message || 'Failed to load recap settings.');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const addTime = () => {
    if (!/^([01]\d|2[0-3]):([0-5]\d)$/.test(newTime)) return;
    if (state.times.includes(newTime)) { setNewTime(''); return; }
    setState((s) => ({ ...s, times: [...s.times, newTime].sort() }));
    setNewTime('');
  };
  const removeTime = (t) => setState((s) => ({ ...s, times: s.times.filter((x) => x !== t) }));

  const save = async () => {
    setError('');
    setSavedOk(false);
    // Fold in a time typed into the picker but not yet "Add"-ed, so Save just works.
    const pending = newTime.trim();
    let times = state.times;
    if (/^([01]\d|2[0-3]):([0-5]\d)$/.test(pending) && !times.includes(pending)) {
      times = [...times, pending].sort();
      setState((s) => ({ ...s, times }));
      setNewTime('');
    }
    if (state.enabled && !state.email.trim()) { setError('Enter an email address.'); return; }
    // Recaps only actually SEND when enabled AND there's at least one time — but you
    // can still save (e.g. just change the email). Enabled without a time saves as
    // off (nothing to send) and we say so, instead of blocking the save.
    const effectiveEnabled = state.enabled && times.length > 0;
    setSaving(true);
    try {
      const r = await settingsAPI.updateEmailRecap({
        enabled: effectiveEnabled,
        email: state.email.trim(),
        timezone: state.timezone,
        times
      });
      const d = r?.data || {};
      // Keep the checkbox as the user set it (their intent); only sync email/tz/times.
      setState((s) => ({
        ...s,
        email: d.email ?? s.email,
        timezone: d.timezone ?? s.timezone,
        times: Array.isArray(d.times) ? d.times : s.times
      }));
      setSavedOk(state.enabled && times.length === 0
        ? 'Saved. Add a time below to start receiving recaps.'
        : 'Saved.');
    } catch (err) {
      if (err.status === 404) setNotReady(true);
      else setError(err.body?.message || err.message || 'Save failed.');
    } finally {
      setSaving(false);
    }
  };

  const tzKnown = TIMEZONES.some(([v]) => v === state.timezone);

  return (
    <section className="card mb-8">
      <div className="flex items-center gap-2 mb-1">
        <Mail size={18} className="text-alfred-gold" />
        <h2 className="text-xl text-stone-100">Email call recaps</h2>
      </div>
      <p className="text-sm text-stone-400 mb-4">
        Get an emailed summary of your recent calls at the times you choose.
      </p>

      {notReady && (
        <p className="mb-4 rounded-lg border border-amber-800/50 bg-amber-950/30 px-4 py-2 text-sm text-amber-200">
          Recaps are being set up on the backend — this will go live shortly.
        </p>
      )}
      {error && (
        <p className="mb-4 rounded-lg border border-red-500/40 bg-red-500/10 px-4 py-2 text-sm text-red-200">{error}</p>
      )}
      {savedOk && (
        <p className="mb-4 rounded-lg border border-emerald-800/50 bg-emerald-950/30 px-4 py-2 text-sm text-emerald-200">{savedOk}</p>
      )}

      {loading ? (
        <p className="text-sm text-stone-500">Loading…</p>
      ) : (
        <div className="space-y-4 max-w-xl">
          <label className="flex items-center gap-2 text-sm text-stone-200">
            <input
              type="checkbox"
              checked={state.enabled}
              onChange={(e) => setState({ ...state, enabled: e.target.checked })}
            />
            Email me a recap of my calls
          </label>

          <div>
            <label className="field-label">Send to</label>
            <input
              className="field-input"
              type="email"
              placeholder="you@example.com"
              value={state.email}
              onChange={(e) => setState({ ...state, email: e.target.value })}
            />
          </div>

          <div>
            <label className="field-label">Time zone</label>
            <select
              className="field-input"
              value={state.timezone}
              onChange={(e) => setState({ ...state, timezone: e.target.value })}
            >
              {!tzKnown && <option value={state.timezone}>{state.timezone}</option>}
              {TIMEZONES.map(([v, label]) => <option key={v} value={v}>{label}</option>)}
            </select>
          </div>

          <div>
            <label className="field-label">Times</label>
            <div className="flex flex-wrap gap-2 mb-2">
              {state.times.length === 0 && <span className="text-sm text-stone-500">No times yet.</span>}
              {state.times.map((t) => (
                <span key={t} className="badge bg-stone-800 text-stone-200 border border-stone-700 inline-flex items-center gap-1">
                  {pretty(t)}
                  <button type="button" onClick={() => removeTime(t)} aria-label={`Remove ${t}`} className="text-stone-400 hover:text-red-300">
                    <X size={12} />
                  </button>
                </span>
              ))}
            </div>
            <div className="flex items-center gap-2">
              <input
                className="field-input w-auto"
                type="time"
                value={newTime}
                onChange={(e) => setNewTime(e.target.value)}
              />
              <button type="button" className="btn-ghost" onClick={addTime} disabled={!newTime}>
                <Plus size={14} /> Add time
              </button>
            </div>
          </div>

          <button type="button" className="btn-primary" onClick={save} disabled={saving}>
            <Save size={14} /> {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      )}
    </section>
  );
}
